/**
 * Maps a Russian CDEK order status name (as returned by the CDEK API)
 * to an i18n translation key. Returns the i18n key, or null when no
 * mapping is found (caller should fall back to the raw string).
 *
 * Some CDEK status names include a city suffix (e.g. "Выдан на отправку в Москва").
 * Those are matched by prefix so the city part is ignored.
 *
 * @param {string} statusName - Raw Russian status string from the CDEK API.
 * @returns {string|null} i18n key or null.
 */
export function getCdekStatusKey(statusName) {
    if (!statusName) return null;

    // Exact-match table
    const exactMap = {
        "Создан":                          "cdek_status_created",
        "Принят на склад отправителя":     "cdek_status_accepted_at_sender_warehouse",
        "Сдан перевозчику":                "cdek_status_handed_to_carrier",
        "В пути":                          "cdek_status_in_transit",
        "Принят на склад доставки":        "cdek_status_accepted_at_delivery_warehouse",
        "Принят на склад":                 "cdek_status_accepted_at_warehouse",
        "Выдан на доставку":               "cdek_status_out_for_delivery",
        "Вручён":                          "cdek_status_delivered",
        "Вручен":                          "cdek_status_delivered",
        "Не вручён":                       "cdek_status_not_delivered",
        "Не вручен":                       "cdek_status_not_delivered",
        "Возврат":                         "cdek_status_returned",
        "Хранится в пункте выдачи":        "cdek_status_held_at_pickup",
        "Ожидает повторной доставки":      "cdek_status_awaiting_redelivery",
        "Удалён":                          "cdek_status_removed",
        "Недействителен":                  "cdek_status_invalid",
    };

    const exactKey = exactMap[statusName.trim()];
    if (exactKey) return exactKey;

    // Prefix-match for statuses that include a city name suffix
    const prefixMap = [
        { prefix: "Выдан на отправку в",  key: "cdek_status_dispatched_from_city" },
        { prefix: "Выдан на отправку",     key: "cdek_status_dispatched_from_city" },
    ];

    for (const { prefix, key } of prefixMap) {
        if (statusName.startsWith(prefix)) return key;
    }

    return null;
}

/**
 * Maps a Russian CDEK location string (statuses[0].city) to an i18n key.
 * Returns the key for known CDEK generic descriptors; null for real city names.
 *
 * @param {string} city - Raw city/location string from the CDEK API.
 * @returns {string|null} i18n key or null.
 */
export function getCdekCityKey(city) {
    if (!city) return null;

    const normalized = city.trim();

    if (normalized === "Офис СДЭК" || normalized.startsWith("Офис СДЭК")) return "cdek_city_office";
    if (normalized === "Склад СДЭК" || normalized.startsWith("Склад СДЭК")) return "cdek_city_warehouse";
    if (normalized === "Пункт выдачи СДЭК" || normalized.startsWith("Пункт выдачи")) return "cdek_city_pickup_point";

    return null;
}
